/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FunctionComponent, useCallback, useMemo } from 'react';

import { logger } from '@dw/services/logger';
import { THttpMethod, HttpClientError } from '@dw/models/shared';
import { useAppSettings, useSnackbar, useFirebase, useActiveEnrolmentYear } from '@dw/hooks';
import { DEFAULT_ERROR_MESSAGE, ROUTES } from '@dw/constants';
import { cleanJsonResponse, formatQueryParamString } from '@dw/utils';

type TQueryParams = {[key: string]: any};


interface IHttpClientRequestOptions {
    /**
     * Should the user be logged out if API returns 401 or 403 errors. Defaults to `true`
     */
    autoLogOutOnAuthError?: boolean;

    /**
     * Should the error message be shown by default. Defaults to `true`
     */
    autoShowErrorMessages?: boolean;
}

interface IHttpClientContext {
    POST: <T>(url: string, body?: any, file?: File, options?: IHttpClientRequestOptions) => Promise<T>;
    PUT: <T>(url: string, body?: any, options?: IHttpClientRequestOptions) => Promise<T>;
    DELETE: <T>(url: string, queryParams?: TQueryParams, options?: IHttpClientRequestOptions) => Promise<T>;
    GET: <T>(url: string, queryParams?: TQueryParams, options?: IHttpClientRequestOptions) => Promise<T>;
    buildUrl: (url: string, queryParams?: TQueryParams) => string;
}
const initialState: IHttpClientContext = {
    POST: () => Promise.resolve<any>({}),
    PUT: () => Promise.resolve<any>({}),
    DELETE: () => Promise.resolve<any>({}),
    GET: () => Promise.resolve<any>({}),
    buildUrl: () => ''
}
export const HttpClientContext = React.createContext<IHttpClientContext>(initialState);

const extractErrorMessages = (responseBody: any): string[] => {
    if (responseBody.errors && typeof responseBody.errors === 'object' && !Array.isArray(responseBody.errors) && Object.keys(responseBody.errors).length > 0) {
        const validErrors = Object.keys(responseBody.errors).filter(x => !x.startsWith('$'));
        if (validErrors.length > 0) {
            return validErrors.flatMap(key => responseBody.errors[key]);
        }
    } else if (responseBody.detail) {
        return [responseBody.detail];
    } else if (responseBody.message) {
        return [responseBody.message];
    }

    return [DEFAULT_ERROR_MESSAGE];
};

const getHttpClientRequestOptions = (options?: IHttpClientRequestOptions): IHttpClientRequestOptions => {
    const defaultOptions: IHttpClientRequestOptions = {
        autoLogOutOnAuthError: true,
        autoShowErrorMessages: true
    };

    return {
        ...defaultOptions,
        ...(options ?? {})
    }
}

async function downloadResponseAsFile<T>(response: Response): Promise<T> {
    const responseHeaders = Object.fromEntries(response.headers.entries());
    const contentDispositionParts = responseHeaders['content-disposition'].split(';').map(x => x.trim());
    const fileNameParts = contentDispositionParts.find(x => x.toLowerCase().startsWith('filename='))?.split('=') ?? []; 

    const blobResponse = await response.blob();
    const url = window.URL.createObjectURL(blobResponse);
    const a = document.createElement('a');
    a.href = url;
    if (fileNameParts.length > 0) {
        a.download = fileNameParts[fileNameParts.length - 1].replaceAll('"', '');
    }
    document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
    a.click();    
    a.remove();

    return { message: 'File download will begin shortly' } as T
}

interface IHttpClientContextProviderProps {
    children: React.ReactNode;
}
export const HttpClientContextProvider: FunctionComponent<IHttpClientContextProviderProps> = (props: IHttpClientContextProviderProps) => {

    const { appSettings } = useAppSettings();
    const { showError } = useSnackbar();
    const { children } = props;

    const { getAccessToken } = useFirebase();
    const { activeEnrolmentYear } = useActiveEnrolmentYear();

    const buildUrl = useCallback((url: string, params?: TQueryParams): string => {
        const queryString = formatQueryParamString(params);
        return `${url.startsWith('http') ? '' : appSettings.apiBaseUrl}${url}${queryString}`;
    }, [appSettings]);

    const { timezoneName, timezoneOffset } = useMemo(() => ({
        timezoneOffset: new Date().getTimezoneOffset() * -1,
        timezoneName: Intl.DateTimeFormat().resolvedOptions().timeZone
    }), []);

    const buildFetchOptions = useCallback((method: THttpMethod, accessToken: string | null, body?: any, file?: File): RequestInit => {
        const options: RequestInit = {
            method,
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-Timezone-Offset': timezoneOffset.toString(),
                'X-Timezone-Name': timezoneName,
                'X-Active-Enrolment-Year': activeEnrolmentYear.toString(),
            }
        };

        if (file) {
            options.headers = {};
        }

        if (accessToken) {
        options.headers = {
            ...options.headers,
            Authorization: `Bearer ${accessToken}`
        };
        }

        if (!file) {
            options.body = JSON.stringify(body);
        } else if (file) {
            const formData = new FormData();
            formData.append('file', file);
            if (body) {
                formData.append('request', JSON.stringify(body));
            }
            options.body = formData;
        }
        return options;
    }, [timezoneName, timezoneOffset, activeEnrolmentYear]);

    const execute = useCallback(async function <T>(method: THttpMethod, url: string, queryParams?: TQueryParams, body?: any, file?: File, options?: IHttpClientRequestOptions): Promise<T> {
        const fetchUrl = buildUrl(url, queryParams);
        const httpOptions = getHttpClientRequestOptions(options);

        try {
            const accessToken = await getAccessToken();
            const fetchOptions = buildFetchOptions(method, accessToken, body, file);

            const fetchResponse = await fetch(fetchUrl, fetchOptions);
            if (fetchResponse.status === 401 || fetchResponse.status === 403) {
                if (httpOptions.autoLogOutOnAuthError) {
                  window.location.href = ROUTES.Login;
                }

                throw new HttpClientError({
                    statusCode: fetchResponse.status,
                    requestMethod: method,
                    requestUrl: fetchUrl,
                    errorMessages: ['Fetch returned authentication error']
                });
            }

            const responseHeaders = Object.fromEntries(fetchResponse.headers.entries());
            if (responseHeaders['content-disposition'] && responseHeaders['content-disposition'].indexOf('attachment') >= 0) {
                return await downloadResponseAsFile(fetchResponse);
            }

            const response = await fetchResponse.json();
            if (fetchResponse.ok) {
                cleanJsonResponse(response);
            }

            if (!fetchResponse.ok) {
                throw new HttpClientError({
                    statusCode: fetchResponse.status,
                    requestMethod: method,
                    requestUrl: fetchUrl,
                    rawErrors: response.errors,
                    errorMessages: extractErrorMessages(response)
                });
            }

            return response as T;
        } catch (err: any) {

            let httpClientError: HttpClientError;

            if (err instanceof HttpClientError) {
                httpClientError = err;
            } else {
                httpClientError = new HttpClientError({
                    statusCode: 500,
                    requestMethod: method,
                    requestUrl: fetchUrl,
                    errorMessages: [DEFAULT_ERROR_MESSAGE],
                    rawErrors: err
                });

                const errorMessage = (err.message ?? '') as string;
                const errorStack = (err.stack?.toString() ?? '') as string;
                if (!errorMessage.toLowerCase().includes('load failed') && !errorStack.toLowerCase().includes('failed to fetch')) {
                    logger.error(`Error while performing ${method} to "${fetchUrl}"`, httpClientError.errorResponse);
                }
            }

            if (httpOptions.autoShowErrorMessages) {
                showError(httpClientError.firstErrorMessage);
            }

            throw httpClientError;
        }
    }, [buildUrl, getAccessToken, showError]); 

    const POST = useCallback(async function <T>(url: string, body?: any, file?: File, options?: IHttpClientRequestOptions): Promise<T> {
        return await execute('POST', url, undefined, body, file, options);
    }, [execute]);

    const PUT = useCallback(async function <T>(url: string, body?: any, options?: IHttpClientRequestOptions): Promise<T> {
        return await execute('PUT', url, undefined, body, undefined, options);
    }, [execute]);

    const DELETE = useCallback(async function <T>(url: string, queryParams?: TQueryParams, options?: IHttpClientRequestOptions): Promise<T> {
        return await execute('DELETE', url, queryParams, undefined, undefined, options);
    }, [execute]);

    const GET = useCallback(async function GET<T>(url: string, queryParams?: TQueryParams, options?: IHttpClientRequestOptions): Promise<T> {
        return await execute('GET', url, queryParams, undefined, undefined, options);
    }, [execute]);

    const client = useMemo(() => ({
        POST,
        GET,
        DELETE,
        PUT,
        buildUrl
    }), [POST, GET, DELETE, PUT, buildUrl]);

    return (
        <HttpClientContext.Provider value={client}>
            {children}
        </HttpClientContext.Provider>
    )
};

